// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Typography from '@dtci/spellbook/Typography';
import Button from '@dtci/spellbook/Button';
// --- Components
import { PaginationBar } from 'datastage/components/PaginationBar';
import { UsersSearchForm } from './components/search-form';
import { UsersGrid } from './components/grid';
import { UserDrawer } from './components/drawer';
import { TwoRowsLayout } from './layout/two-row';
import ToastNotificationProvider from 'datastage/components/ToastNotifications';
// --- Hooks & Utils
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { getItemRange, getAllItemsRange } from 'datastage/utils/pagination';
import { exportToCsv } from 'datastage/utils/export/exportToCsv';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { useStoredItemsPerPageSync } from 'datastage/utils/hooks/useStoredItemsPerPageSync';
import { PAGINATION_STORAGE_KEYS } from 'datastage/utils/paginationPreferences';
// --- API & Store
import { useUsersActions } from 'datastage/store/users/useUsers';
import { useUsersData } from './hooks/useUsersData';
// --- Config
import * as userRoles from 'datastage/config/userRoles';

/**
 * Maps role IDs to user-friendly labels for CSV export.
 *
 * @param {Array} roleIds - Array of role ID numbers
 * @returns {string} Semicolon-separated role labels
 */
function mapRoles(roleIds) {
    const roleMap = [
        [userRoles.ROLE_ADMINISTRATOR, 'ADMINISTRATOR'],
        [userRoles.ROLE_RIGHTSLINE_PRODUCT_GATE_CONTENT_SALES, 'RIGHTSLINE PRODUCT GATE'],
        [userRoles.ROLE_BULK_DATE_MATH_TOOL, 'BULK DATE MATH TOOL'],
        [userRoles.ROLE_AIR_DATE_INGESTION_TOOL, 'AIR DATE INGESTION TOOL'],
        [userRoles.ROLE_WON_ERROR_REPORT, 'WON ERROR REPORT'],
        [userRoles.ROLE_APOLLO_ERROR_REPORT, 'APOLLO ERROR REPORT'],
        [userRoles.ROLE_JOB_TRACKER, 'JOB TRACKER'],
        [userRoles.ROLE_LEGAL_ADMIN, 'LEGAL ADMIN'],
        [userRoles.ROLE_LEGAL_USER, 'LEGAL USER'],
        [userRoles.ROLE_DEAL_VERSIONING, 'DEAL VERSIONING'],
        [userRoles.ROLE_DEAL_VERSIONING_READ_ONLY, 'DEAL VERSIONING READ ONLY'],
        [userRoles.ROLE_PRODUCT_COMMENTS_ADMIN, 'PRODUCT COMMENTS ADMIN'],
        [userRoles.ROLE_TAP_USER, 'TAP'],
        [userRoles.ROLE_RIGHTSETS_USER, 'RIGHTSETS USER'],
        [userRoles.ROLE_RIGHTSETS_ASSOCIATE_USER, 'RIGHTSETS ASSOCIATE USER'],
    ];
    return roleMap
        .filter(([id]) => roleIds.includes(id))
        .map(([, label]) => label)
        .join('; ');
}

/**
 * UsersPage displays and manages the list of users.
 *
 * Features:
 * - Displays a paginated, searchable, and sortable user grid.
 * - Allows creating and editing users via a unified drawer form.
 * - Supports exporting user data to CSV.
 * - Provides refresh and pagination controls.
 * - Uses Redux for state management and Ant Design/Spellbook for UI.
 * - Implements refreshUsers flag pattern for clean separation of concerns.
 * - Persists page size preference to localStorage automatically.
 *
 * @component
 * @returns {JSX.Element} The rendered users management page.
 */
export const UsersPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showErrorToast } = useToasts();
    const { updateUsers } = useUsersActions();
    const { fetchUsers, userRange, filterForm, userSort, itemsPerPage } = useUsersData();
    const { totalPages, currentPage, totalItems, refreshUsers, pageSizeOptions } = useSelector((s) => s.users);

    /**
     * Sync persisted page size preference from localStorage on mount.
     * If stored preference differs from Redux state, automatically updates Redux.
     */
    useStoredItemsPerPageSync({
        storageKey: PAGINATION_STORAGE_KEYS.users,
        itemsPerPage,
        rangeKey: 'userRange',
        onSync: updateUsers,
    });


    /**
     * Listen for refreshUsers flag and refresh the grid.
     * This pattern allows other components (like drawers) to trigger refreshes
     * without duplicating fetch logic.
     */
    useEffect(() => {
        if (refreshUsers) {
            handleRefresh();
            updateUsers({ refreshUsers: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshUsers]);

    /**
     * Handle page change for pagination (←/→ buttons).
     * Fetches directly without updating URL (TAP-UI pattern).
     * Page size changes go through URL → search form handles those.
     */
    const handlePageChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;

        const nextPageRange = getItemRange(pageNumber, itemsPerPage);
        const data = await fetchUsers(nextPageRange);

        if (data) {
            updateUsers({
                users: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                totalItems: data?.totalElements ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                userRange: nextPageRange,
            });
        }
    };

    /**
     * Handle page size change from dropdown.
     * Updates URL with new page size - the search form will handle the fetch.
     * This prevents duplicate fetches (TAP-UI pattern).
     */
    const handlePageSizeChange = (newSize) => {
        if (newSize === itemsPerPage) return;

        // Update URL with new page size
        // Search form will see URL change and fetch with new size
        const currentParams = parseSearchParams(location.search);
        updateSearchParams(location, navigate, {
            ...currentParams,
            pageSize: newSize,
        });
    };

    /**
     * Refresh the user list from the API.
     */
    const handleRefresh = async () => {
        const data = await fetchUsers(userRange);

        if (data) {
            updateUsers({
                users: data?.content ?? [],
                totalItems: data?.totalElements ?? 0,
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
            });
        }
    };

    /**
     * Export all users to CSV.
     * Fetches all records using the total count from the current result set.
     */
    const handleExport = async () => {
        // Fetch all users for export using dynamic range based on total items
        const exportRange = getAllItemsRange(totalItems);
        const data = await fetchUsers(exportRange);

        if (data?.code) {
            showErrorToast(data.message || 'An unexpected error occurred');
            return;
        }

        const usersExport = (data?.content || []).map((user) => ({
            id: user.id,
            email: user.username,
            fullName: user.fullName || user.username,
            rightslineId: user.rightslineId || '',
            roles: mapRoles(user.spaUserRoleIds),
            createdAt: user.createdAt,
            modifiedAt: user.modifiedAt,
        }));
        exportToCsv(usersExport, 'users');
    };

    /**
     * Open the create user drawer.
     */
    const handleCreateUser = () => {
        updateUsers({
            drawerMode: 'create',
            selectedUser: {},
        });
    };

    return (
        <>
            <Row
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 20,
                }}
            >
                <Col>
                    <Typography variant="T50" style={{ marginLeft: 20 }}>
                        Users
                    </Typography>
                </Col>
                <Col style={{ display: 'flex', gap: 8 }}>
                    <Button
                        iconType="fa-solid fa-plus"
                        labelText="Create"
                        styleType="link"
                        onClick={handleCreateUser}
                    >
                        Create
                    </Button>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                        disabled={totalItems === 0}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            <TwoRowsLayout>
                <main data-position="top">
                    <UsersSearchForm />
                </main>
                <main data-position="bottom">
                    <UsersGrid />
                </main>
            </TwoRowsLayout>

            <PaginationBar
                totalPages={totalPages}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeOptions}
            />

            {/* Unified drawer for create/edit */}
            <UserDrawer />

            <ToastNotificationProvider />
        </>
    );
};
